import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, match } from "react-router-dom";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import {
  CloseOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
  EditOutlined
} from "@ant-design/icons";
import { Layout, Row, Button, Popover, Tag } from "antd";
import Slider from "react-slick";
import { useMedia } from "react-use";
import "video-react/dist/video-react.css";

import * as actions from "../../actions";
import DownloadBtn from "../../components/downloadbtn";
import { getPrevNextItem } from "../../selectors";
import { formatDate } from "../../utils";

import { LanguageState } from "../../reducers/language";
import { FiltersState } from "../../reducers/filters";
import { ItemsState } from "../../reducers/items";
import { PrevNextItem } from "../../selectors/items";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import noImage from "../../images/no-image.png";
import { State } from "../../interfaces";
import FileLoader from "../../components/FileLoader";

const { Content, Header } = Layout;

interface Props {
  match: match<any>;
}

const Details = (props: Props) => {
  const thumbs = useRef<any>();
  const { match } = props;

  const [, setType] = useState("image");
  const isSmall = useMedia("(max-width: 768px)");

  const filters: FiltersState = useSelector(
    (state: State) => state.filters,
    shallowEqual
  );
  const { byId: tagById } = filters.tags.entities;

  const language: LanguageState = useSelector(
    (state: State) => state.language,
    shallowEqual
  );
  const { labels } = language;

  const items: ItemsState = useSelector(
    (state: State) => state.items,
    shallowEqual
  );
  const { entities, selectedItem, userCanShare } = items;

  const nav: PrevNextItem = useSelector(
    (state: State) => getPrevNextItem(state.items),
    shallowEqual
  );

  const dispatch = useDispatch();

  const intervalRef = useRef<any>();

  useEffect(() => {
    const getItemFromID = (id: string) => {
      if (selectedItem !== null && selectedItem !== undefined) {
        const index = entities.all.indexOf(selectedItem.id);
        intervalRef.current = setTimeout(
          () => thumbs.current.slickGoTo(index),
          300
        );
      } else {
        dispatch(actions.getItemById(id));
      }
    };
    getItemFromID(match.params.itemId);

    return () => clearTimeout(intervalRef.current);
  }, [entities.all, match.params.itemId, dispatch, selectedItem]);

  useEffect(() => {
    if (selectedItem) {
      const index = selectedItem.previewType.indexOf("/");
      setType(selectedItem.previewType.substring(0, index));
      // console.log("selectedItem ", type);
    }
  }, [selectedItem]);

  const onBack = () => {
    dispatch(actions.selectItem(null));
  };

  const onPrev = useCallback(() => {
    thumbs.current.slickPrev();
    dispatch(actions.selectItem(nav.prevItem));
  }, [dispatch, nav.prevItem]);

  const onNext = useCallback(() => {
    thumbs.current.slickNext();
    dispatch(actions.selectItem(nav.nextItem));
  }, [dispatch, nav.nextItem]);

  const onSelectThumb = useCallback(
    (item) => {
      dispatch(actions.selectItem(item));
    },
    [dispatch]
  );

  // console.log("object");

  return (
    <Layout className="details">
      <Header style={{ position: "fixed", width: "100%" }}>
        {selectedItem && (
          <Row justify="space-between" align="middle">
            <Link to="/home" onClick={onBack}>
              <Button className="nav-bnt back-btn" type="default">
                <CloseOutlined />
              </Button>
            </Link>
            <h2 className="details-title">{selectedItem.name}</h2>

            {!isSmall ? (
              <Popover
                overlayClassName="popover-item-info"
                placement="bottomRight"
                getPopupContainer={(triggerNode) =>
                  triggerNode.parentNode as HTMLElement
                }
                content={
                  <>
                    <h4>{formatDate(selectedItem.publishDate)}</h4>
                    {selectedItem.tags &&
                      selectedItem.tags.map((tag) => {
                        return (
                          <Tag key={tag} className="item-tag" color="#999">
                            {tagById && tagById[tag].name}
                          </Tag>
                        );
                      })}
                  </>
                }
              >
                <Button className="info-btn">
                  <InfoCircleOutlined />
                </Button>
              </Popover>
            ) : null}
            {labels && (
              <DownloadBtn
                direction="bottomRight"
                files={selectedItem.files}
                label={isSmall ? "" : `${labels.download}`}
                itemId={selectedItem.id}
              />
            )}
            {labels && userCanShare && (
              <Button
                className="edit-btn btn-grey"
                title={labels.edit}
                href={selectedItem.edit_link}
                target="_blank"
              >
                <EditOutlined />
              </Button>
            )}
          </Row>
        )}
      </Header>
      <Content className="details-content">
        {selectedItem && (
          <div className="details-wrapper">
            <div className="thumbs-wrapper">
              <Link
                to={`/details/${nav.prevItem !== null ? nav.prevItem?.id : ""}`}
                onClick={onPrev}
              >
                <Button
                  className="nav-bnt"
                  type="default"
                  disabled={nav.prevItem === null}
                  size="large"
                >
                  <LeftOutlined />
                  {/* {isSmall ? "" : "Prev"} */}
                </Button>
              </Link>
              <div className="thumbs">
                <Slider
                  ref={thumbs}
                  autoplay={false}
                  speed={500}
                  slidesToShow={5}
                  slidesToScroll={1}
                  arrows={false}
                  responsive={[
                    { breakpoint: 768, settings: { slidesToShow: 3 } },
                    { breakpoint: 1024, settings: { slidesToShow: 5 } }
                  ]}
                >
                  {entities.all.map((itemId) => {
                    const item = entities.byId[itemId];

                    const preview = item.thumbUrl || noImage;

                    return (
                      <Link
                        key={itemId}
                        to={`/details/${itemId}`}
                        onClick={() => onSelectThumb(item)}
                      >
                        <FileLoader
                          clssName={
                            "thumb-image " +
                            (itemId === selectedItem.id ? "active" : "")
                          }
                          fileUrl={preview}
                          type="css"
                        />
                      </Link>
                    );
                  })}
                </Slider>
              </div>
              <Link
                to={`/details/${nav.nextItem !== null ? nav.nextItem?.id : ""}`}
                onClick={onNext}
              >
                <Button
                  className="nav-bnt"
                  type="default"
                  disabled={nav.nextItem === null}
                  size="large"
                >
                  {/* {isSmall ? "" : "Next"} */}
                  <RightOutlined />
                </Button>
              </Link>
            </div>
            <FileLoader
              clssName="detail-image"
              fileUrl={selectedItem.previewUrl}
              type="css"
            />
          </div>
        )}
      </Content>
    </Layout>
  );
};

export default Details;
